import React, { useState } from "react";
import { Plus, Info, ChevronRight, Settings, Truck, Tent } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const hotspots = [{
  id: 1,
  label: "Suspension & Tires",
  icon: <Settings className="h-4 w-4" />,
  top: "78%",
  left: "24%",
  specs: [{
    label: "Tires",
    value: "37\" M/T Baja Boss"
  }, {
    label: "Suspension",
    value: "2.5\" Remote Reservoir"
  }, {
    label: "Clearance",
    value: "14 Inches"
  }, {
    label: "PSI (Hwy/Trail)",
    value: "45 / 18"
  }]
}, {
  id: 2,
  label: "Habitation Module",
  icon: <Tent className="h-4 w-4" />,
  top: "35%",
  left: "68%",
  specs: [{
    label: "Shell",
    value: "Composite Panel"
  }, {
    label: "Sleeps",
    value: "2 Adults + 1 Child"
  }, {
    label: "Power",
    value: "400Ah Lithium"
  }, {
    label: "Water",
    value: "40 Gallons"
  }]
}, {
  id: 3,
  label: "Recovery & Storage",
  icon: <Truck className="h-4 w-4" />,
  top: "18%",
  left: "45%",
  specs: [{
    label: "Rack",
    value: "Low Profile Modular"
  }, {
    label: "Lighting",
    value: "360° Scene Lights"
  }, {
    label: "Recovery",
    value: "12k lb Winch"
  }, {
    label: "Storage",
    value: "150L Weatherproof"
  }]
}];
export default function TheRig() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [openPopover, setOpenPopover] = useState<number | null>(null);
  return <section id="the-rig" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-10">
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562326/img/rig_blueprint_wireframe.png)] bg-cover bg-center bg-no-repeat mix-blend-overlay" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-transparent to-background" />
      </div>

      <div className="container relative z-10 mx-auto px-4">
        <div className="mb-12 text-center">
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-wide text-primary mb-4">
            The Rig
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Built for the long haul. Explore the technical specifications of our
            expedition vehicle designed for the American West.
          </p>
        </div>

        {}
        <div ref={ref} className={`relative mx-auto max-w-5xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="relative aspect-[16/9] md:aspect-[21/9] w-full rounded-xl border border-border/50 bg-card/30 backdrop-blur-sm shadow-2xl overflow-hidden group">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562326/img/expedition_truck_side_profile.png" alt="Expedition Truck Side Profile" className="h-full w-full object-cover object-center opacity-90 transition-transform duration-700 group-hover:scale-105" />

            {}
            <div className="absolute inset-0 bg-gradient-to-t from-background/40 to-transparent pointer-events-none" />

            {}
            {hotspots.map((spot, i) => <div key={spot.id} data-index={i} className="absolute" style={{
            top: spot.top,
            left: spot.left
          }}>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="default" size="icon" className="group relative h-8 w-8 rounded-full p-0 shadow-lg ring-2 ring-background hover:scale-110 transition-transform overflow-visible" aria-label={`View specs for ${spot.label}`}>
                      {}
                      <span className="absolute inset-0 inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75 duration-1000 pointer-events-none" />
                      
                      {}
                      <Plus className="size-5 relative z-10" />

                      {}
                      <span className="absolute left-full ml-3 hidden whitespace-nowrap rounded-md bg-background/80 px-2 py-1 text-xs font-bold uppercase tracking-wider text-foreground backdrop-blur-md md:block opacity-0 group-hover:opacity-100 transition-opacity border border-border pointer-events-none">
                        {spot.label}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent side="top" className="w-64 border-primary/20 bg-card/95 p-0 backdrop-blur-xl">
                    <div className="border-b border-border p-3 bg-muted/30">
                      <div className="flex items-center gap-2 text-sm font-bold text-primary uppercase tracking-wider">
                        {spot.icon}
                        {spot.label}
                      </div>
                    </div>
                    <div className="p-3">
                      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                        {spot.specs.map((spec, idx) => <div key={idx} data-index={idx} className="col-span-2 flex justify-between border-b border-border/50 pb-1 last:border-0 last:pb-0">
                            <dt className="text-muted-foreground font-mono">{spec.label}</dt>
                            <dd className="font-medium text-foreground font-mono text-right">{spec.value}</dd>
                          </div>)}
                      </dl>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>)}
          </div>

          {}
          <div className="absolute inset-0 pointer-events-none border border-white/5 rounded-xl">
            <div className="absolute top-4 left-4 size-16 border-l border-t border-white/20" />
            <div className="absolute top-4 right-4 size-16 border-r border-t border-white/20" />
            <div className="absolute bottom-4 left-4 size-16 border-l border-b border-white/20" />
            <div className="absolute bottom-4 right-4 size-16 border-r border-b border-white/20" />
            <div className="absolute top-1/2 left-0 w-full h-px bg-white/5" />
            <div className="absolute left-1/2 top-0 h-full w-px bg-white/5" />
          </div>
        </div>

        {}
        <div className="mt-12 flex flex-col items-center justify-center gap-6">
          <div className="flex items-center gap-4 text-sm text-muted-foreground font-mono">
            <span className="flex items-center gap-1">
              <Info className="size-4" />
              Click markers for details
            </span>
            <span className="h-4 w-px bg-border" />
            <span>Model: 2024 Overland Pro</span>
          </div>
          
          <Link to="/TheRigsPage">
            <Button size="lg" className="group font-bold tracking-wide">
              View Full Build List
              <ChevronRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}